import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity } from '../../common';
import {
  DateRangeState,
  FilterRoute,
  getSelectedTimeRange,
  useAppSelector,
} from '@zonofi/zono-money-store';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import RadioButtonCheckedOutlinedIcon from '@mui/icons-material/RadioButtonCheckedOutlined';
import {
  DATE_RANGE_FILTERS,
  RELATIVE_TIME_RANGE_FILTERS,
} from './date-range-filters';
import { BottomModal } from '../bottom-modal';
import { DateRangeSelector } from '../date-range';
import moment from 'moment';
import { useCustomDateRange } from '../../hooks';
import { DateRangeKeys, RelativeTimeRange } from '../../types';
import { RELATIVE_TIME_RANGE_ROUTES } from './defined-filter';

interface DateFilterItemProps {
  data: DateRangeState;
  dateRangeSelection: DateRangeState | null;
  setDateRangeSelection: (val: DateRangeState | null) => void;
  dateSelectionMode?: 'date' | 'dateRange' | undefined;
  route?: FilterRoute;
  localTimeRange: RelativeTimeRange | null;
  setLocalTimeRange: (val: RelativeTimeRange | null) => void;
}

export const DateFilterItem: React.FC<DateFilterItemProps> = ({
  data,
  setDateRangeSelection,
  dateRangeSelection,
  dateSelectionMode = 'dateRange',
  route,
  localTimeRange,
  setLocalTimeRange,
}) => {
  const selectedTimeRange = useAppSelector(getSelectedTimeRange);
  const isTimeRangeMode = route
    ? RELATIVE_TIME_RANGE_ROUTES.includes(route)
    : false;

  const { showCalender, setShowCalender, dateRangeFromUrl, handleSelect } =
    useCustomDateRange(
      route,
      dateRangeSelection as DateRangeState,
      setDateRangeSelection,
      dateSelectionMode
    );

  const [selectedRange, setSelectedRange] = useState<DateRangeState>({
    startDate: data?.startDate,
    endDate: data?.endDate,
  });

  const isUserActionRef = useRef(false);

  useEffect(() => {
    if (selectedTimeRange) {
      setLocalTimeRange(selectedTimeRange);
    }
  }, [selectedTimeRange]);

  useEffect(() => {
    if (isUserActionRef.current) {
      isUserActionRef.current = false;
      return;
    }

    const isStartSame = moment(selectedRange.startDate).isSame(
      data.startDate,
      'day'
    );
    const isEndSame = moment(selectedRange.endDate).isSame(data.endDate, 'day');

    if (!isStartSame || !isEndSame) {
      setSelectedRange({
        startDate: data?.startDate,
        endDate: data?.endDate,
      });
    }
  }, [data]);

  const displayItems = useMemo(() => {
    if (isTimeRangeMode) {
      return Object.values(RELATIVE_TIME_RANGE_FILTERS).map(
        ({ key, label }) => ({
          id: key,
          value: label,
          selected: localTimeRange === key,
        })
      );
    }

    const items = Object.entries(DATE_RANGE_FILTERS).map(
      ([key, { startDate, endDate }]) => {
        const isStartSame = moment(startDate).isSame(
          selectedRange?.startDate,
          'day'
        );
        const isEndSame = moment(endDate).isSame(selectedRange?.endDate, 'day');

        return {
          id: key,
          value: key,
          selected: isStartSame && isEndSame,
        };
      }
    );

    const custom = {
      id: 'Custom',
      value: 'Custom',
      selected: items.every((item) => !item.selected),
    };

    items.push(custom);
    return items;
  }, [isTimeRangeMode, selectedRange, localTimeRange]);

  const onPress = (id: string) => {
    isUserActionRef.current = true;

    if (isTimeRangeMode) {
      setLocalTimeRange(id as RelativeTimeRange);
      return;
    }

    if (id === 'Custom') {
      setDateRangeSelection(null);
      setShowCalender(true);
    } else {
      const { startDate, endDate } = DATE_RANGE_FILTERS[id as DateRangeKeys];
      const normalizedRange = {
        startDate: moment(startDate).startOf('day').toDate(),
        endDate: moment(endDate).endOf('day').toDate(),
      };

      setSelectedRange(normalizedRange);
      setDateRangeSelection(normalizedRange);
    }
  };

  const formatDateRange = (startDate: Date, endDate: Date) => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (start.year() === end.year()) {
      return `${start.format('DD MMM')} - ${end.format('DD MMM, YYYY')}`;
    } else {
      return `${start.format('DD MMM, YYYY')} - ${end.format('DD MMM, YYYY')}`;
    }
  };

  return (
    <View className="filter-modal__sort-group">
      <Text className="filter-modal__sort-title font-semibold">
        {'BY DATE'}
      </Text>

      <View className="filter-modal__sort-options">
        {displayItems.map(({ id, value, selected }) => (
          <TouchableOpacity
            key={id}
            className="filter-modal__sort-option-view"
            onPress={() => onPress(id)}
          >
            {selected ? (
              <RadioButtonCheckedOutlinedIcon className="filter-radio-checked" />
            ) : (
              <RadioButtonUncheckedOutlinedIcon className="filter-radio-unchecked" />
            )}
            <View className="filter-modal__sort-option-text">
              <Text
                className={`filter-modal__sort-option-text-primary font-medium ${
                  selected ? 'selected-value' : ''
                }`}
              >
                {value}
              </Text>
              {dateRangeSelection?.startDate &&
                dateRangeSelection?.endDate &&
                selected &&
                id === 'Custom' && (
                  <Text className="filter-modal__sort-option-text-secondary font-medium">
                    (
                    {formatDateRange(
                      dateRangeSelection.startDate,
                      dateRangeSelection.endDate
                    )}
                    )
                  </Text>
                )}
            </View>
          </TouchableOpacity>
        ))}
      </View>

      <BottomModal
        title={'Date Selection'}
        leftText={'Close'}
        onLeftAction={() => setShowCalender(false)}
        rightText={'Reset'}
        isVisible={showCalender}
        onRightAction={() => {
          setShowCalender(false);
          setDateRangeSelection(null);
        }}
        showDivider
        bottomButton={'Apply'}
        onBottomAction={() => {
          isUserActionRef.current = true;
          setSelectedRange(dateRangeSelection as DateRangeState);
          setShowCalender(false);
        }}
      >
        <DateRangeSelector
          dateState={[
            {
              key: 'selection',
              startDate: moment(
                dateRangeSelection?.startDate ?? dateRangeFromUrl?.startDate
              ).toDate(),
              endDate: moment(
                dateRangeSelection?.endDate ?? dateRangeFromUrl?.endDate
              ).toDate(),
            },
          ]}
          onDateChange={handleSelect}
          dateSelectionMode={dateSelectionMode}
        />
      </BottomModal>
    </View>
  );
};
